import {
  ProductOffering,
  ProductOfferingOrderType,
  type Resource,
} from '@travelwin/core';
import { useCallback, useMemo, useState } from 'react';

import CountryCoverageModal from '../../CountryCoverageModal';
import PlanBenefitTile from '../../PlanBenefitTile/PlanBenefitTile';

export const PlanBenefitCoverageTile = ({
  countries,
  planType,
}: {
  countries: Resource[];
  planType: ProductOfferingOrderType;
}) => {
  const [countryCoverageModal, setCountryCoverageModal] = useState(false);

  if (countries.length === 2) {
    return (
      <PlanBenefitTile
        key="countries"
        type="countries"
        value={countries.length}
        countryKeys={countries.map((value) => value.description)}
        planType={planType}
      />
    );
  } else if (countries.length > 2) {
    return (
      <>
        <PlanBenefitTile
          key="countries"
          type="countries"
          value={countries.length}
          onClick={(e) => {
            e.stopPropagation();
            setCountryCoverageModal(true);
          }}
          planType={planType}
        />

        <CountryCoverageModal
          key="countries-coverage"
          on={countryCoverageModal}
          onCloseButton={() => setCountryCoverageModal(false)}
          countries={countries}
        />
      </>
    );
  }

  return null;
};

export const PlanBenefits = ({
  planDetails: {
    bundle_validity,
    countries,
    texts,
    call_minutes,
    call_minutes_international,
    calls_international,
    calls_local,
  },
  planType,
}: {
  planDetails: ProductOffering;
  planType: ProductOfferingOrderType;
}) => {
  const getMinutesBreakdown = useCallback(() => {
    if (calls_international && !calls_local) {
      return {
        local: 0,
        international: call_minutes,
      };
    }

    return {
      local: call_minutes,
      international: call_minutes_international,
    };
  }, [
    call_minutes,
    call_minutes_international,
    calls_international,
    calls_local,
  ]);

  const planBenefits = useMemo(() => {
    const minutes = getMinutesBreakdown();

    const benefitTiles = [];

    benefitTiles.push(
      <PlanBenefitCoverageTile
        countries={countries}
        planType={planType}
        key="country"
      />,
    );

    benefitTiles.push(
      <PlanBenefitTile
        key="bundle_validity"
        type="bundle_validity"
        value={bundle_validity}
      />,
    );

    benefitTiles.push(
      <PlanBenefitTile
        key="call_minutes"
        type="call_minutes"
        value={minutes.local}
      />,
    );
    benefitTiles.push(
      <PlanBenefitTile
        key="call_minutes_international"
        type="call_minutes_international"
        value={minutes.international}
      />,
    );
    benefitTiles.push(
      <PlanBenefitTile key="texts" type="texts" value={texts} />,
    );

    return benefitTiles;
  }, [bundle_validity, countries, getMinutesBreakdown, planType, texts]);

  return <>{planBenefits}</>;
};
